import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch, formatLagosDate } from "./api";

const stages = [
  "TSS stage",
  "Allotment of Project to SBC",
  "Delivery of Materials to site",
  "Completion of Installation works",
  "Testing, Commissioning, and Handover",
  "Site work complete"
];

const expenseCategories = [
  { value: "SBC", label: "SBC" },
  { value: "NEMSA", label: "NEMSA" },
  { value: "DISCO", label: "DISCO" },
  { value: "PR", label: "PR (Area boys, Landlord, Community)" },
  { value: "Logistics", label: "Logistics" },
  { value: "Material", label: "Materials" },
  { value: "Other", label: "Other" }
];

const documentCategories = [
  "TSS + BOQ submission to ATC",
  "ATC PO",
  "DISCO approval",
  "GAVL-SBC PO",
  "NEMSA approval",
  "NOC",
  "JCC",
  "Office Track",
  "GAVL Invoice to ATC",
  "ATC approved Invoice",
  "Proof of payment by ATC",
  "Pictures of site"
];

const siteFinanceUsers = new Set([
  "kandarp.g@globusafriventures.com",
  "radhey.g@globusafriventures.com"
]);

function normalizeExpenseCategory(category) {
  if (category === "Area Boys") return "PR";
  if (category === "Materials") return "Material";
  return category || "Other";
}

function roundMoney(value) {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "";
}

function moneyValue(value) {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? Number(amount.toFixed(2)) : 0;
}

function formatMoney(value) {
  const amount = parseFloat(value);
  if (!Number.isFinite(amount)) return "0.00";
  return amount.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function SiteDetail({ user }) {
  const { id } = useParams();
  const siteId = id;
  const isAdmin = Boolean(user?.is_admin);
  const canViewSiteFinance = siteFinanceUsers.has((user?.email || "").toLowerCase());

  const [site, setSite] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [selectedBoq, setSelectedBoq] = useState(null);
  const [boqMaterials, setBoqMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [issues, setIssues] = useState([]);
  const [issueStatusFilter, setIssueStatusFilter] = useState("All");
  const [updates, setUpdates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentFiles, setDocumentFiles] = useState({});
  const [newExpense, setNewExpense] = useState({ category: "", description: "", amount: "" });
  const [newPayment, setNewPayment] = useState({ type: "", description: "", amount: "" });
  const [newIssue, setNewIssue] = useState({ title: "", description: "", stage: "", assigned_to: "" });
  const [newUpdate, setNewUpdate] = useState({ title: "", description: "" });

  const selectedBoqObj =
    Array.isArray(boqs) && selectedBoq
      ? boqs.find(b => String(b.id) === String(selectedBoq))
      : null;

  const selectedBoqLocked = selectedBoqObj?.is_locked === "Yes";
  const siteLocked = site?.is_locked === "Yes";

  const filteredMaterials = materials.filter(mat =>
    (mat.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIssues = issues.filter(issue =>
    issueStatusFilter === "All" || (issue.status || "Open") === issueStatusFilter
  );

  const boqTotal = boqMaterials.reduce((sum, material) => {
    const qty = parseFloat(material.quantity) || 0;
    const cost = moneyValue(material.estimated_cost);
    return sum + qty * cost;
  }, 0);
  const boqVat = boqTotal * 0.075;
  const boqTotalWithVat = boqTotal + boqVat;

  const totalExpense = expenses.reduce((sum, expense) => {
    return sum + (parseFloat(expense.amount) || 0);
  }, 0);

  const totalReceived = payments.reduce((sum, payment) => {
    return sum + (parseFloat(payment.amount) || 0);
  }, 0);

  const expenseTotals = expenseCategories.map(category => {
    const total = expenses
      .filter(expense => normalizeExpenseCategory(expense.category) === category.value)
      .reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0);
    return { ...category, total };
  });

  const documentsByCategory = documentCategories.reduce((acc, category) => {
    acc[category] = documents.filter(doc => doc.category === category);
    return acc;
  }, {});

  function loadSite() {
    return apiFetch(`/api/site/${siteId}`)
      .then(res => res.json())
      .then(data => setSite(data));
  }

  function loadBoqs() {
    return apiFetch(`/api/boq/${siteId}`)
      .then(res => res.json())
      .then(data => {
        const boqList = Array.isArray(data) ? data : [];
        setBoqs(boqList);
        if (!selectedBoq && boqList.length > 0) {
          setSelectedBoq(boqList[0].id);
        }
        return boqList;
      });
  }

  function loadBoqMaterials(boqId = selectedBoq) {
    if (!boqId) {
      setBoqMaterials([]);
      return Promise.resolve([]);
    }

    return apiFetch(`/api/site-material/${boqId}`)
      .then(res => res.json())
      .then(data => {
        const rows = Array.isArray(data) ? data : [];
        setBoqMaterials(rows);
        return rows;
      });
  }

  function loadExpenses() {
    return apiFetch(`/api/expense/${siteId}`)
      .then(res => res.json())
      .then(data => setExpenses(Array.isArray(data) ? data : []));
  }

  function loadPayments() {
    return apiFetch(`/api/payment/${siteId}`)
      .then(res => res.json())
      .then(data => setPayments(Array.isArray(data) ? data : []));
  }

  function loadIssues() {
    return apiFetch(`/api/issue/${siteId}`)
      .then(res => res.json())
      .then(data => setIssues(Array.isArray(data) ? data : []));
  }

  function loadUpdates() {
    return apiFetch(`/api/update/${siteId}`)
      .then(res => res.json())
      .then(data => setUpdates(Array.isArray(data) ? data : []));
  }

  function loadDocuments() {
    return apiFetch(`/api/document/${siteId}`)
      .then(res => res.json())
      .then(data => setDocuments(Array.isArray(data) ? data : []));
  }

  useEffect(() => {
    loadSite();
    apiFetch("/api/materials")
      .then(res => res.json())
      .then(data => setMaterials(Array.isArray(data) ? data : []));
    loadBoqs();
    loadExpenses();
    loadPayments();
    loadIssues();
    loadUpdates();
    loadDocuments();
  }, [siteId]);

  useEffect(() => {
    loadBoqMaterials(selectedBoq);
  }, [selectedBoq]);

  function showResult(data, fallbackMessage) {
    if (data?.error || data?.detail) {
      alert(data.error || data.detail);
      return false;
    }
    if (fallbackMessage) alert(fallbackMessage);
    return true;
  }

  function updateSiteStatus(status) {
    const params = new URLSearchParams({ id: site.id, status });
    apiFetch(`/api/site/status?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setSite(prev => ({ ...prev, status }));
      });
  }

  function updateSbc(assignedSbc) {
    const params = new URLSearchParams({ id: site.id, assigned_sbc: assignedSbc });
    apiFetch(`/api/site/sbc?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) {
          loadSite();
          return;
        }
        setSite(prev => ({ ...prev, assigned_sbc: data.assigned_sbc }));
        setSaveStatus("Saved");
        setTimeout(() => setSaveStatus(""), 1500);
      });
  }

  function lockSite() {
    if (!window.confirm("Lock this site permanently?")) return;
    const params = new URLSearchParams({ id: siteId });
    apiFetch(`/api/site/lock?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data, "Site locked")) return;
        setSite(prev => ({ ...prev, is_locked: "Yes" }));
      });
  }

  function unlockSite() {
    if (!window.confirm("Unlock this site?")) return;
    const params = new URLSearchParams({ id: siteId });
    apiFetch(`/api/site/unlock?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data, "Site unlocked")) return;
        setSite(prev => ({ ...prev, is_locked: "No" }));
      });
  }

  function createBoq(type) {
    const params = new URLSearchParams({ site_id: siteId, version_type: type });
    apiFetch(`/api/boq/create?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setSelectedBoq(data.id);
        loadBoqs();
      });
  }

  function syncBoq(targetType) {
    const params = new URLSearchParams({ site_id: siteId, target_type: targetType });
    apiFetch(`/api/boq/sync?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data, `Synced ${data.copied || 0} material(s)`)) return;
        loadBoqs();
        if (selectedBoqObj?.version_type === targetType) {
          loadBoqMaterials();
        }
      });
  }

  function lockBoq(boqId) {
    const params = new URLSearchParams({ boq_id: boqId });
    apiFetch(`/api/boq/lock?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data, "BOQ locked")) return;
        loadBoqs();
      });
  }

  function unlockBoq(boqId) {
    const params = new URLSearchParams({ boq_id: boqId });
    apiFetch(`/api/boq/unlock?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data, "BOQ unlocked")) return;
        loadBoqs();
      });
  }

  function addMaterial(materialId) {
    if (siteLocked || selectedBoqLocked) {
      alert("This BOQ or site is locked");
      return;
    }

    if (!selectedBoq) {
      alert("Select a BOQ first");
      return;
    }

    if (boqMaterials.some(row => row.material_id === materialId)) {
      alert("Material already added");
      return;
    }

    const params = new URLSearchParams({
      site_id: siteId,
      material_id: materialId,
      boq_version_id: selectedBoq
    });

    apiFetch(`/api/site-material/add?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        loadBoqMaterials();
      });
  }

  function addCustomMaterial() {
    if (siteLocked || selectedBoqLocked) {
      alert("This BOQ or site is locked");
      return;
    }

    if (!selectedBoq) {
      alert("Select a BOQ first");
      return;
    }

    const params = new URLSearchParams({
      site_id: siteId,
      boq_version_id: selectedBoq
    });

    apiFetch(`/api/site-material/add-custom?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        loadBoqMaterials();
      });
  }

  function updateMaterial(id, updates) {
    const params = new URLSearchParams({ id, ...updates });
    apiFetch(`/api/site-material/update?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) {
          loadBoqMaterials();
          return;
        }

        setBoqMaterials(prev => prev.map(row => (
          row.id === id ? { ...row, ...updates } : row
        )));
        setSaveStatus("Saved");
        setTimeout(() => setSaveStatus(""), 1500);
        loadBoqMaterials();
      });
  }

  function deleteBoqMaterial(id) {
    if (!window.confirm("Delete this BOQ item?")) return;
    const params = new URLSearchParams({ id });
    apiFetch(`/api/site-material/delete?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setBoqMaterials(prev => prev.filter(row => row.id !== id));
      });
  }

  function addUpdate() {
    const params = new URLSearchParams({
      site_id: siteId,
      title: newUpdate.title,
      description: newUpdate.description
    });

    apiFetch(`/api/update/add?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setNewUpdate({ title: "", description: "" });
        loadUpdates();
      });
  }

  function deleteUpdate(id) {
    if (!window.confirm("Delete this update?")) return;
    const params = new URLSearchParams({ id });
    apiFetch(`/api/update/delete?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setUpdates(prev => prev.filter(update => update.id !== id));
      });
  }

  function addIssue() {
    if (!newIssue.title || !newIssue.description) {
      alert("Fill all issue fields");
      return;
    }

    const params = new URLSearchParams({
      site_id: siteId,
      title: newIssue.title,
      description: newIssue.description,
      stage: newIssue.stage,
      assigned_to: newIssue.assigned_to
    });

    apiFetch(`/api/issue/add?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setNewIssue({ title: "", description: "", stage: "", assigned_to: "" });
        loadIssues();
      });
  }

  function updateIssueStatus(issueId, status) {
    const params = new URLSearchParams({ id: issueId, status });
    apiFetch(`/api/issue/status?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setIssues(prev => prev.map(issue => (
          issue.id === issueId ? { ...issue, status } : issue
        )));
      });
  }

  function deleteIssue(issueId) {
    if (!window.confirm("Delete this issue?")) return;
    const params = new URLSearchParams({ id: issueId });
    apiFetch(`/api/issue/delete?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setIssues(prev => prev.filter(issue => issue.id !== issueId));
      });
  }

  function addExpense() {
    if (!newExpense.category || !newExpense.description || !newExpense.amount) {
      alert("Fill all expense fields");
      return;
    }

    const params = new URLSearchParams({
      site_id: siteId,
      category: newExpense.category,
      description: newExpense.description,
      amount: newExpense.amount
    });

    apiFetch(`/api/expense/add?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setNewExpense({ category: "", description: "", amount: "" });
        loadExpenses();
      });
  }

  function updateExpense(id, updates) {
    const params = new URLSearchParams({ id, ...updates });
    apiFetch(`/api/expense/update?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) {
          loadExpenses();
          return;
        }
        setExpenses(prev => prev.map(expense => (
          expense.id === id ? { ...expense, ...updates } : expense
        )));
      });
  }

  function deleteExpense(id) {
    if (!window.confirm("Delete this expense?")) return;
    const params = new URLSearchParams({ id });
    apiFetch(`/api/expense/delete?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setExpenses(prev => prev.filter(expense => expense.id !== id));
      });
  }

  function addPayment() {
    if (!newPayment.type || !newPayment.description || !newPayment.amount) {
      alert("Fill all payment fields");
      return;
    }

    const params = new URLSearchParams({
      site_id: siteId,
      type: newPayment.type,
      description: newPayment.description,
      amount: newPayment.amount
    });

    apiFetch(`/api/payment/add?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setNewPayment({ type: "", description: "", amount: "" });
        loadPayments();
      });
  }

  function deletePayment(id) {
    if (!window.confirm("Delete this payment?")) return;
    const params = new URLSearchParams({ id });
    apiFetch(`/api/payment/delete?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setPayments(prev => prev.filter(payment => payment.id !== id));
      });
  }

  function uploadDocument(category) {
    const file = documentFiles[category];
    if (!file) {
      alert("Select a file first");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const params = new URLSearchParams({
      site_id: siteId,
      category
    });

    apiFetch(`/api/document/upload?${params}`, {
      method: "POST",
      body: formData
    })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data, "Document uploaded")) return;
        setDocumentFiles(prev => ({ ...prev, [category]: null }));
        loadDocuments();
      });
  }

  function downloadDocument(documentId) {
    apiFetch(`/api/document/download/${documentId}`)
      .then(res => {
        if (!res.ok) throw new Error("Download failed");
        return res.blob().then(blob => ({ blob, res }));
      })
      .then(({ blob, res }) => {
        const disposition = res.headers.get("content-disposition") || "";
        const filenamePart = disposition
          .split(";")
          .find(part => part.trim().startsWith("filename="));
        const filename = filenamePart
          ? filenamePart.split("=").slice(1).join("=").replaceAll('"', "").trim()
          : "document";
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => alert(err.message));
  }

  function deleteDocument(documentId) {
    if (!window.confirm("Delete this document?")) return;
    const params = new URLSearchParams({ document_id: documentId });
    apiFetch(`/api/document/delete?${params}`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (!showResult(data)) return;
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      });
  }

  function downloadTssPdf() {
    apiFetch(`/api/pdf/tss/${siteId}`)
      .then(res => {
        if (!res.ok) throw new Error("PDF download failed");
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      })
      .catch(err => alert(err.message));
  }

  function downloadExecPdf() {
    apiFetch(`/api/pdf/exec/${siteId}`)
      .then(res => {
        if (!res.ok) throw new Error("EXEC PO download failed");
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      })
      .catch(err => alert(err.message));
  }

  if (!site) return <div>Loading...</div>;

  return (
    <div style={{ padding: "20px" }}>
      <button onClick={() => window.location.href = "/atc"}>
        Back to ATC Sites
      </button>

      <h2>{site.name}</h2>

      {siteLocked ? (
        <>
          <span style={{ marginLeft: "15px", color: "red", fontWeight: "bold" }}>
            Locked for editing
          </span>
          {isAdmin && (
            <button style={{ marginLeft: "10px" }} onClick={unlockSite}>
              Unlock Site
            </button>
          )}
        </>
      ) : (
        <button style={{ marginLeft: "10px" }} onClick={lockSite}>
          Lock Site
        </button>
      )}

      <button style={{ marginLeft: "10px" }} onClick={downloadTssPdf}>
        Download TSS BOQ PDF
      </button>

      <button style={{ marginLeft: "10px" }} onClick={downloadExecPdf}>
        Download EXEC PO PDF
      </button>

      <div style={card}>
        <h3>Basic Info</h3>
        <p>Status: {site.status}</p>
        <p>DISCO: {site.disco}</p>
        <label>
          SBC:
          <input
            style={{ marginLeft: "8px" }}
            defaultValue={site.assigned_sbc || ""}
            onBlur={(e) => updateSbc(e.target.value)}
          />
        </label>
      </div>

      <h3>Update Status</h3>
      <select
        value={site.status || ""}
        disabled={siteLocked}
        onChange={(e) => updateSiteStatus(e.target.value)}
      >
        {stages.map(stage => (
          <option key={stage} value={stage}>{stage}</option>
        ))}
      </select>

      <div style={card}>
        <h3>Updates</h3>
        <input
          placeholder="Update title"
          value={newUpdate.title}
          disabled={siteLocked}
          onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
        />
        <input
          placeholder="Update description"
          value={newUpdate.description}
          disabled={siteLocked}
          onChange={(e) => setNewUpdate({ ...newUpdate, description: e.target.value })}
        />
        <button disabled={siteLocked} onClick={addUpdate}>Add Update</button>

        <ul>
          {updates.map(update => (
            <li key={update.id}>
              <b>{update.title}</b>
              <br />
              {update.description}
              <br />
              {formatLagosDate(update.created_at)}
              {isAdmin && (
                <button style={{ marginLeft: "10px" }} onClick={() => deleteUpdate(update.id)}>
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div style={card}>
        <h3>Issues</h3>

        <div>
          <input
            placeholder="Title"
            value={newIssue.title}
            disabled={siteLocked}
            onChange={(e) => setNewIssue({ ...newIssue, title: e.target.value })}
          />
          <input
            placeholder="Description"
            value={newIssue.description}
            disabled={siteLocked}
            onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
          />
          <select
            value={newIssue.stage}
            disabled={siteLocked}
            onChange={(e) => setNewIssue({ ...newIssue, stage: e.target.value })}
          >
            <option value="">Stage</option>
            {stages.map(stage => (
              <option key={stage} value={stage}>{stage}</option>
            ))}
          </select>
          <input
            placeholder="Assigned To"
            value={newIssue.assigned_to}
            disabled={siteLocked}
            onChange={(e) => setNewIssue({ ...newIssue, assigned_to: e.target.value })}
          />
          <button disabled={siteLocked} onClick={addIssue}>Add Issue</button>
        </div>

        <div style={{ marginTop: "10px" }}>
          <label>Status filter: </label>
          <select
            value={issueStatusFilter}
            onChange={(e) => setIssueStatusFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Closed">Closed</option>
          </select>
        </div>

        <ul>
          {filteredIssues.map(issue => (
            <li key={issue.id}>
              <b>{issue.title}</b> - {issue.stage} ({issue.assigned_to})
              <br />
              {issue.description}
              <br />
              {formatLagosDate(issue.created_at)}
              <br />
              Status:
              <select
                value={issue.status || "Open"}
                disabled={siteLocked}
                onChange={(e) => updateIssueStatus(issue.id, e.target.value)}
              >
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Closed">Closed</option>
              </select>
              {isAdmin && (
                <button style={{ marginLeft: "10px" }} onClick={() => deleteIssue(issue.id)}>
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div style={card}>
        <h3>BOQ Versions</h3>

        {boqs.map(boq => (
          <div key={boq.id} style={{ marginBottom: "10px" }}>
            <button
              onClick={() => setSelectedBoq(boq.id)}
              style={{
                marginRight: "10px",
                padding: "8px",
                backgroundColor: String(selectedBoq) === String(boq.id) ? "#4CAF50" : "#ddd"
              }}
            >
              {boq.version_type}
            </button>

            {boq.is_locked === "Yes" ? (
              <>
                <span style={{ color: "red" }}>Locked</span>
                {isAdmin && (
                  <button style={{ marginLeft: "10px" }} onClick={() => unlockBoq(boq.id)}>
                    Unlock
                  </button>
                )}
              </>
            ) : (
              <button onClick={() => lockBoq(boq.id)}>Lock</button>
            )}
          </div>
        ))}

        <div style={{ marginTop: "10px" }}>
          <button disabled={siteLocked} onClick={() => createBoq("TSS")}>+ TSS</button>
          <button disabled={siteLocked} onClick={() => createBoq("PO")}>+ PO</button>
          <button disabled={siteLocked} onClick={() => createBoq("EXECUTION")}>+ EXEC</button>
        </div>

        <div style={{ marginTop: "10px" }}>
          <button disabled={siteLocked} onClick={() => syncBoq("PO")}>Sync PO from TSS</button>
          <button disabled={siteLocked} onClick={() => syncBoq("EXECUTION")} style={{ marginLeft: "10px" }}>
            Sync Execution from PO
          </button>
        </div>
      </div>

      <div style={card}>
        <h3>BOQ Materials</h3>

        <input
          type="text"
          placeholder="Search material..."
          value={searchTerm}
          disabled={siteLocked || selectedBoqLocked}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: "8px", width: "300px", marginBottom: "10px" }}
        />

        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ccc", padding: "5px" }}>
          {filteredMaterials.map(material => (
            <div
              key={material.id}
              onClick={() => addMaterial(material.id)}
              style={{ padding: "5px", cursor: siteLocked || selectedBoqLocked ? "not-allowed" : "pointer", borderBottom: "1px solid #eee" }}
            >
              {material.name} {material.unit ? `(${material.unit})` : ""}
            </div>
          ))}
        </div>

        {saveStatus && <div style={{ color: "green", marginBottom: "10px" }}>{saveStatus}</div>}
        {!selectedBoq && <p>Select a BOQ first</p>}

        {selectedBoq && (
          <table border="1" cellPadding="10">
            <thead>
              <tr>
                <th>Material</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Responsibility</th>
                <th>Cost</th>
                <th>Total</th>
                <th>Procured</th>
                <th>Installed</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {boqMaterials.map(material => {
                const qty = parseFloat(material.quantity) || 0;
                const cost = moneyValue(material.estimated_cost);
                const total = qty * cost;
                const disabled = siteLocked || selectedBoqLocked;

                return (
                  <tr key={material.id}>
                    <td>
                      {material.is_custom ? (
                        <input
                          placeholder="Extra material / work item"
                          defaultValue={material.material_name || ""}
                          disabled={disabled}
                          onBlur={(e) => updateMaterial(material.id, { custom_name: e.target.value })}
                        />
                      ) : (
                        material.material_name
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        defaultValue={material.quantity || ""}
                        disabled={disabled}
                        onBlur={(e) => updateMaterial(material.id, { quantity: e.target.value })}
                      />
                    </td>
                    <td>{material.unit}</td>
                    <td>
                      <select
                        defaultValue={material.responsibility || ""}
                        disabled={disabled}
                        onChange={(e) => updateMaterial(material.id, { responsibility: e.target.value })}
                      >
                        <option value="">Select</option>
                        <option value="ATC">ATC</option>
                        <option value="SBC">SBC</option>
                        <option value="GAVL">GAVL</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={roundMoney(material.estimated_cost)}
                        disabled={disabled}
                        onBlur={(e) => updateMaterial(material.id, { estimated_cost: roundMoney(e.target.value) })}
                      />
                    </td>
                    <td>{formatMoney(total)}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={material.procured === "Yes"}
                        disabled={disabled}
                        onChange={(e) => updateMaterial(material.id, { procured: e.target.checked ? "Yes" : "No" })}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={material.installed === "Yes"}
                        disabled={disabled}
                        onChange={(e) => updateMaterial(material.id, { installed: e.target.checked ? "Yes" : "No" })}
                      />
                    </td>
                    <td>
                      <button disabled={disabled} onClick={() => deleteBoqMaterial(material.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <button
          disabled={siteLocked || selectedBoqLocked || !selectedBoq}
          onClick={addCustomMaterial}
          style={{ marginTop: "10px" }}
        >
          Add Blank BOQ Row
        </button>

        <h3>VAT at 7.5%: ₦ {formatMoney(boqVat)}</h3>
        <h3>Total Incl. VAT: ₦ {formatMoney(boqTotalWithVat)}</h3>
      </div>

      <div style={card}>
        <h3>Documents</h3>
        <p style={{ color: "red", fontWeight: "bold" }}>
          Pls upload only compressed documents and pictures.
        </p>

        {documentCategories.map(category => (
          <div key={category} style={{ borderTop: "1px solid #ddd", paddingTop: "10px", marginTop: "10px" }}>
            <h4>{category}</h4>
            <input
              type="file"
              disabled={siteLocked}
              onChange={(e) => setDocumentFiles(prev => ({
                ...prev,
                [category]: e.target.files?.[0] || null
              }))}
            />
            <button
              disabled={siteLocked || !documentFiles[category]}
              onClick={() => uploadDocument(category)}
            >
              Upload
            </button>

            <ul>
              {(documentsByCategory[category] || []).map(doc => (
                <li key={doc.id}>
                  <button onClick={() => downloadDocument(doc.id)}>
                    Download
                  </button>
                  <span style={{ marginLeft: "8px" }}>
                    {doc.original_filename}
                    {doc.uploaded_by ? ` - ${doc.uploaded_by}` : ""}
                    {doc.created_at ? ` - ${formatLagosDate(doc.created_at)}` : ""}
                  </span>
                  {isAdmin && (
                    <button
                      style={{ marginLeft: "10px" }}
                      disabled={siteLocked}
                      onClick={() => deleteDocument(doc.id)}
                    >
                      Delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div style={card}>
        <h3>Expenses</h3>

        <div style={{ marginBottom: "10px" }}>
          <select
            value={newExpense.category}
            disabled={siteLocked}
            onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
          >
            <option value="">Select Category</option>
            {expenseCategories.map(category => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>
          <input
            placeholder="Description"
            value={newExpense.description}
            disabled={siteLocked}
            onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
          />
          <input
            type="number"
            placeholder="Amount"
            value={newExpense.amount}
            disabled={siteLocked}
            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
          />
          <button disabled={siteLocked} onClick={addExpense}>Add Expense</button>
        </div>

        <table border="1" cellPadding="8" style={{ marginBottom: "15px" }}>
          <thead>
            <tr>
              <th>Category</th>
              <th>Total Spent (₦)</th>
            </tr>
          </thead>
          <tbody>
            {expenseTotals.map(category => (
              <tr key={category.value}>
                <td>{category.label}</td>
                <td>{formatMoney(category.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <ul>
          {expenses.map(expense => (
            <li key={expense.id}>
              <select
                value={normalizeExpenseCategory(expense.category)}
                disabled={siteLocked}
                onChange={(e) => updateExpense(expense.id, { category: e.target.value })}
              >
                {expenseCategories.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
              <input
                value={expense.description || ""}
                disabled={siteLocked}
                onChange={(e) => updateExpense(expense.id, { description: e.target.value })}
              />
              <input
                value={expense.amount || ""}
                disabled={siteLocked}
                onChange={(e) => updateExpense(expense.id, { amount: e.target.value })}
              />
              {formatLagosDate(expense.created_at)}
              {isAdmin && (
                <button style={{ marginLeft: "10px" }} onClick={() => deleteExpense(expense.id)}>
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>

        {canViewSiteFinance && (
          <>
            <h3>Payments</h3>
            <div style={{ marginBottom: "10px" }}>
              <select
                value={newPayment.type}
                disabled={siteLocked}
                onChange={(e) => setNewPayment({ ...newPayment, type: e.target.value })}
              >
                <option value="">Select Type</option>
                <option value="Advance">Advance</option>
                <option value="Final">Final</option>
                <option value="Adjustment">Adjustment</option>
              </select>
              <input
                placeholder="Description"
                value={newPayment.description}
                disabled={siteLocked}
                onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
              />
              <input
                type="number"
                placeholder="Amount"
                value={newPayment.amount}
                disabled={siteLocked}
                onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
              />
              <button disabled={siteLocked} onClick={addPayment}>Add Payment</button>
            </div>

            <ul>
              {payments.map(payment => (
                <li key={payment.id}>
                  {payment.type} - ₦{payment.amount} ({payment.description})
                  {payment.created_at ? ` ${formatLagosDate(payment.created_at)}` : ""}
                  {isAdmin && (
                    <button style={{ marginLeft: "10px" }} onClick={() => deletePayment(payment.id)}>
                      Delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        <h3>BOQ Value (Estimated): ₦ {formatMoney(boqTotal)}</h3>
        <h3>Total Expense (Actual): ₦ {formatMoney(totalExpense)}</h3>
        {canViewSiteFinance && (
          <>
            <h3>Total Payment Received (ATC): ₦ {formatMoney(totalReceived)}</h3>
            <h3>Net Profit: ₦ {formatMoney(totalReceived - totalExpense)}</h3>
          </>
        )}
      </div>
    </div>
  );
}

const card = {
  border: "1px solid #ddd",
  padding: "15px",
  marginTop: "20px",
  marginBottom: "20px",
  borderRadius: "10px",
  backgroundColor: "#f9f9f9"
};

export default SiteDetail;
